import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { membersApi } from "../api/membersApi";
import { FaLinkedin } from "react-icons/fa";

const Members = () => {
  const [members, setMembers] = useState([]);
  // console.log("Members state:", members);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [departments, setDepartments] = useState([]);

  // Base URL for server-hosted assets (dev default to localhost:5000)
  const API_BASE = process.env.REACT_APP_API_URL || "";

  // Inline SVG fallback to avoid external DNS failures
  const FALLBACK_SVG = `data:image/svg+xml;utf8,${encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' width='300' height='300'><rect width='100%' height='100%' fill='%23e5e7eb'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%236b7280' font-family='Arial, Helvetica, sans-serif' font-size='20'>No Image</text></svg>"
  )}`;

  const resolveImageSrc = (imagePath) => {
    if (!imagePath) return FALLBACK_SVG;
    // If absolute URL, return as-is
    if (/^https?:\/\//i.test(imagePath)) return imagePath;
    // If path starts with /uploads, serve from API server
    if (imagePath.startsWith("/uploads")) return `${API_BASE}${imagePath}`;
    // Otherwise return as-is
    return imagePath;
  };

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const response = await membersApi.getPublicMembers();
        // console.log("Fetched members:", response);
        // setMembers(response.data.data);
        if (response.success) {
          setMembers(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching members:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, []);

  const filteredMembers = members
    .filter(
      (member) =>
        member.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.designation.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.department.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(
      (member) =>
        selectedDepartment === "" || member.department === selectedDepartment
    )
    .sort((a, b) => a.priority - b.priority);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <section className="min-h-screen bg-gradient-to-br from-gray-50 to-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-20">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Executive Committee
            </h1>
            <h2 className="text-3xl font-semibold text-[#672f89]">
              Association for Industrial Development
            </h2>
          </div>

          {/* Check if there are members */}
          {filteredMembers.length === 0 ? (
            <div className="text-center py-28">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-gray-200 rounded-3xl mb-6">
                <svg
                  className="w-12 h-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                  />
                </svg>
              </div>
              <p className="text-3xl text-gray-600 font-medium">
                No members found.
              </p>
            </div>
          ) : (
            <>
              {/* Split members into three sections based on priority */}
              {(() => {
                const top5Members = filteredMembers.slice(0, 5);
                const middleMembers = filteredMembers
                  .slice(5)
                  .filter((member) => member.priority < 50);
                const thirdSectionMembers = filteredMembers.filter(
                  (member) => member.priority >= 50
                );

                return (
                  <>
                    {/* Top 5 Priority Members Section */}
                    {top5Members.length > 0 && (
                      <>
                        <div className="mb-16">
                          <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">
                            {/* Core Committee */}
                          </h2>

                          {/* Top row with 3 members */}
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mb-10">
                            {top5Members.slice(0, 3).map((member) => (
                              <div
                                key={member._id}
                                className="bg-white rounded-3xl shadow-xl border border-gray-200 p-10 text-center hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-3 relative"
                              >
                                <div className="w-40 h-40 mx-auto mb-8 bg-gray-200 rounded-full overflow-hidden border-4 border-white shadow-lg">
                                  {member.profileImage ? (
                                    <img
                                      src={resolveImageSrc(member.profileImage)}
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = FALLBACK_SVG;
                                      }}
                                      className="w-full h-full object-cover"
                                      alt={member.fullName}
                                    />
                                  ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-[#672f89] to-[#8b5eaf] flex items-center justify-center">
                                      <span className="text-white text-4xl font-bold">
                                        {member.fullName
                                          .split(" ")
                                          .map((n) => n[0])
                                          .join("")}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <h3 className="text-3xl font-bold text-gray-900 mb-6 leading-tight">
                                  {member.fullName}
                                </h3>
                                <div className="w-24 h-1.5 bg-[#672f89] mx-auto mb-6"></div>
                                <p className="text-2xl text-gray-700 leading-relaxed font-medium">
                                  {member.designation}
                                </p>
                                {/* LinkedIn Link */}
                                {member.social?.linkedin && (
                                  <a
                                    href={member.social.linkedin}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 mt-4 text-[#0077b5] hover:text-[#005582] transition-colors text-lg font-medium"
                                  >
                                    <FaLinkedin className="w-8 h-8" />
                                    Connect on LinkedIn
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Bottom row with 2 members - centered */}
                          {top5Members.length > 3 && (
                            <div className="flex justify-center">
                              <div
                                className="grid grid-cols-1 md:grid-cols-2 gap-10 w-full lg:max-w-[calc(66.666%+20px)]"
                              >
                                {top5Members.slice(3, 5).map((member) => (
                                  <div
                                    key={member._id}
                                    className="bg-white rounded-3xl shadow-xl border border-gray-200 p-10 text-center hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-3 relative"
                                  >
                                    <div className="w-40 h-40 mx-auto mb-8 bg-gray-200 rounded-full overflow-hidden border-4 border-white shadow-lg">
                                      {member.profileImage ? (
                                        <img
                                          src={resolveImageSrc(
                                            member.profileImage
                                          )}
                                          onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = FALLBACK_SVG;
                                          }}
                                          className="w-full h-full object-cover"
                                          alt={member.fullName}
                                        />
                                      ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-[#672f89] to-[#8b5eaf] flex items-center justify-center">
                                          <span className="text-white text-4xl font-bold">
                                            {member.fullName
                                              .split(" ")
                                              .map((n) => n[0])
                                              .join("")}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    <h3 className="text-3xl font-bold text-gray-900 mb-6 leading-tight">
                                      {member.fullName}
                                    </h3>
                                    <div className="w-24 h-1.5 bg-[#672f89] mx-auto mb-6"></div>
                                    <p className="text-2xl text-gray-700 leading-relaxed font-medium">
                                      {member.designation}
                                    </p>
                                    {/* LinkedIn Link */}
                                    {member.social?.linkedin && (
                                      <a
                                        href={member.social.linkedin}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 mt-4 text-[#0077b5] hover:text-[#005582] transition-colors text-lg font-medium"
                                      >
                                        <FaLinkedin className="w-6 h-6" />
                                        Connect on LinkedIn
                                      </a>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Divider between sections */}
                        {middleMembers.length > 0 && (
                          <div className="flex justify-center my-24">
                            <div className="w-48 h-1 bg-gray-300"></div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Middle Members Section (Priority < 20) */}
                    {middleMembers.length > 0 && (
                      <>
                        <div>
                          <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">
                            {/* Committee Members */}
                          </h2>
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                            {middleMembers.map((member) => (
                              <div
                                key={member._id}
                                className="bg-white rounded-3xl shadow-xl border border-gray-200 p-10 text-center hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-3 relative"
                              >
                                <div className="w-40 h-40 mx-auto mb-8 bg-gray-200 rounded-full overflow-hidden border-4 border-white shadow-lg">
                                  {member.profileImage ? (
                                    <img
                                      src={resolveImageSrc(member.profileImage)}
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = FALLBACK_SVG;
                                      }}
                                      className="w-full h-full object-cover"
                                      alt={member.fullName}
                                    />
                                  ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center">
                                      <span className="text-white text-4xl font-bold">
                                        {member.fullName
                                          .split(" ")
                                          .map((n) => n[0])
                                          .join("")}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <h3 className="text-3xl font-bold text-gray-900 mb-6 leading-tight">
                                  {member.fullName}
                                </h3>
                                <div className="w-24 h-1.5 bg-[#672f89] mx-auto mb-6"></div>
                                <p className="text-2xl text-gray-700 leading-relaxed font-medium">
                                  {member.designation}
                                </p>
                                {/* LinkedIn Link */}
                                {member.social?.linkedin && (
                                  <a
                                    href={member.social.linkedin}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 mt-4 text-[#0077b5] hover:text-[#005582] transition-colors text-lg font-medium"
                                  >
                                    <FaLinkedin className="w-6 h-6" />
                                    Connect on LinkedIn
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Divider between sections */}
                        {thirdSectionMembers.length > 0 && (
                          <div className="flex justify-center my-24">
                            <div className="w-48 h-1 bg-gray-300"></div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Third Section Members (Priority >= 20) */}
                    {thirdSectionMembers.length > 0 && (
                      <div>
                        <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">
                          {/* Additional Members */}
                        </h2>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                          {thirdSectionMembers.map((member) => (
                            <div
                              key={member._id}
                              className="bg-white rounded-3xl shadow-xl border border-gray-200 p-10 text-center hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-3 relative"
                            >
                              <div className="w-40 h-40 mx-auto mb-8 bg-gray-200 rounded-full overflow-hidden border-4 border-white shadow-lg">
                                {member.profileImage ? (
                                  <img
                                    src={resolveImageSrc(member.profileImage)}
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = FALLBACK_SVG;
                                    }}
                                    className="w-full h-full object-cover"
                                    alt={member.fullName}
                                  />
                                ) : (
                                  <div className="w-full h-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center">
                                    <span className="text-white text-4xl font-bold">
                                      {member.fullName
                                        .split(" ")
                                        .map((n) => n[0])
                                        .join("")}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <h3 className="text-3xl font-bold text-gray-900 mb-6 leading-tight">
                                {member.fullName}
                              </h3>
                              <div className="w-24 h-1.5 bg-[#672f89] mx-auto mb-6"></div>
                              <p className="text-2xl text-gray-700 leading-relaxed font-medium">
                                {member.designation}
                              </p>
                              {/* LinkedIn Link */}
                              {member.social?.linkedin && (
                                <a
                                  href={member.social.linkedin}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 mt-4 text-[#0077b5] hover:text-[#005582] transition-colors text-lg font-medium"
                                >
                                  <FaLinkedin className="w-6 h-6" />
                                  Connect on LinkedIn
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Members;